// ThemeContext.js
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider as MuiThemeProvider, responsiveFontSizes } from "@mui/material/styles";
import { createContext, useEffect, useMemo, useState } from "react";

import GlobalStyles, { PrintStyles } from "./GlobalStyles";

// Contrast detection utilities
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
};

const getLuminance = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0.5;

    const [r, g, b] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map(val => {
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastRatio = (color1, color2) => {
    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
};

const hasLowContrast = (color1, color2, threshold = 3.0) => {
    return getContrastRatio(color1, color2) < threshold;
};

const DEFAULT_PALETTE = {
    light: {
        primary: { main: "#0C2340" },
        secondary: { main: "#C4CED3" },
        background: { default: "#ffffff" },
        paper: { main: "#fafafa" }
    },
    dark: {
        primary: { main: "#8EB5DC" },
        secondary: { main: "#7d6d00" },
        background: { default: "#000" },
        paper: { main: "#202020" }  // Contrasts with default for AppBar/surfaces
    },
    fontSize: 15,
    fontFamily: "Open Sans",
    h1: { fontSize: "3.5rem", fontWeight: 200 },
    h2: { fontSize: "3.0rem", fontWeight: 200 },
    h3: { fontSize: "2.5rem", fontWeight: 300 },
    h4: { fontSize: "2.0rem", fontWeight: 400 },
    h5: { fontSize: "1.5rem", fontWeight: 500 },
    h6: { fontSize: "1.0rem", fontWeight: 600 },
    body1: { fontSize: "1rem", lineHeight: 1.5 },
    body2: { fontSize: "0.9rem", lineHeight: 1.4 },
    button: {
        fontSize: "0.8rem",
        textTransform: "none",
        textDecoration: "none"
    }
};

const DEFAULT_TYPOGRAPHY = {
    fontFamily: "Open Sans"
};

const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
    const [darkMode, setDarkMode] = useState(localStorage.getItem("themeMode") ? localStorage.getItem("themeMode") !== "false" : false);
    const [fontFamily, setFamily] = useState(
        localStorage.getItem("themeFont") ? localStorage.getItem("themeFont") :
            "Open Sans");

    // Branding settings state
    const [brandingSettings, setBrandingSettings] = useState({
        customLogoBase64: null,
        organizationName: "",
        organizationShortName: "",
        logoUrl: null,
        logoDarkUrl: null,
        logoIconUrl: null,
        logoIconDarkUrl: null,
        palette: DEFAULT_PALETTE,
        typography: DEFAULT_TYPOGRAPHY
    });

    // Load branding settings from localStorage on mount
    useEffect(() => {
        try {
            const logoBase64 = localStorage.getItem('logo_base64');
            const brandStyles = localStorage.getItem('brandstyles');

            let settings = {
                customLogoBase64: null,
                organizationName: "",
                organizationShortName: "",
                logoUrl: null,
                logoDarkUrl: null,
                logoIconUrl: null,
                logoIconDarkUrl: null,
                palette: DEFAULT_PALETTE,
                typography: DEFAULT_TYPOGRAPHY
            };

            if (logoBase64) {
                settings.customLogoBase64 = logoBase64;
            }

            if (brandStyles) {
                const parsedStyles = JSON.parse(brandStyles);
                settings = {
                    ...settings,
                    ...parsedStyles,
                    customLogoBase64: logoBase64 // Ensure logo is always from its own key
                };
            }

            console.log("ORG BRAND STYLES", settings);

            /*
            setBrandingSettings(settings);
            */

        } catch (error) {
            console.error('Error loading branding settings from localStorage:', error);
        }
    }, []);

    // Branding functions
    const updateBrandingSettings = (partial) => {
        const newSettings = { ...brandingSettings, ...partial };
        setBrandingSettings(newSettings);

        // Save to localStorage
        try {
            const { customLogoBase64, ...settingsWithoutLogo } = newSettings;

            if (customLogoBase64) {
                localStorage.setItem('logo_base64', customLogoBase64);
            } else {
                localStorage.removeItem('logo_base64');
            }

            localStorage.setItem('brandstyles', JSON.stringify(settingsWithoutLogo));
        } catch (error) {
            console.error('Error saving branding settings to localStorage:', error);
        }
    };

    const updateLogo = (base64) => {
        updateBrandingSettings({ customLogoBase64: base64 });
    };

    const updateOrganizationName = (name) => {
        updateBrandingSettings({ organizationName: name });
    };

    const updateOrganizationShortName = (shortName) => {
        updateBrandingSettings({ organizationShortName: shortName });
    };

    const updateLogoUrl = (url) => {
        updateBrandingSettings({ logoUrl: url });
    };

    const updateLogoDarkUrl = (url) => {
        updateBrandingSettings({ logoDarkUrl: url });
    };

    const updateLogoIconUrl = (url) => {
        updateBrandingSettings({ logoIconUrl: url });
    };

    const updateLogoIconDarkUrl = (url) => {
        updateBrandingSettings({ logoIconDarkUrl: url });
    };

    const resetBrandingSettings = () => {
        const defaultSettings = {
            customLogoBase64: null,
            organizationName: "",
            organizationShortName: "",
            logoUrl: null,
            logoDarkUrl: null,
            logoIconUrl: null,
            logoIconDarkUrl: null,
            palette: DEFAULT_PALETTE,
            typography: DEFAULT_TYPOGRAPHY
        };
        setBrandingSettings(defaultSettings);
        localStorage.removeItem('logo_base64');
        localStorage.removeItem('brandstyles');
    };

    const theme = useMemo(() => {
        // Get colors from branding settings or use defaults
        const getPrimaryColor = () => {
            if (brandingSettings?.palette) {
                return darkMode
                    ? brandingSettings.palette.dark.primary.main
                    : brandingSettings.palette.light.primary.main;
            }
            return darkMode ? "#0C2340" : "#0C2340";
        };

        const getSecondaryColor = () => {
            if (brandingSettings?.palette) {
                return darkMode
                    ? brandingSettings.palette.dark.secondary.main
                    : brandingSettings.palette.light.secondary.main;
            }
            return darkMode ? "#ceb812" : "#C4CED3";
        };

        // Get font family from branding settings or use current
        const getFontFamily = () => {
            if (brandingSettings?.typography?.fontFamily) {
                return brandingSettings.typography.fontFamily;
            }
            return fontFamily;
        };

        // Get background color from branding settings or defaults
        const getBackgroundColor = () => {
            if (brandingSettings?.palette) {
                return darkMode
                    ? brandingSettings.palette.dark.background?.default || DEFAULT_PALETTE.dark.background.default
                    : brandingSettings.palette.light.background?.default || DEFAULT_PALETTE.light.background.default;
            }
            return darkMode ? DEFAULT_PALETTE.dark.background.default : DEFAULT_PALETTE.light.background.default;
        };

        const getPaperColor = () => {
            if (brandingSettings?.palette) {
                return darkMode
                    ? brandingSettings.palette.dark.paper?.main || DEFAULT_PALETTE.dark.paper.main
                    : brandingSettings.palette.light.paper?.main || DEFAULT_PALETTE.light.paper.main;
            }
            return darkMode ? DEFAULT_PALETTE.dark.paper.main : DEFAULT_PALETTE.light.paper.main;
        };

        const plt = {
            mode: darkMode ? "dark" : "light",
            background: {
                default: getBackgroundColor(),
                paper: getPaperColor()
            },
            primary: {
                main: getPrimaryColor()
            },
            secondary: {
                main: getSecondaryColor()
            }
        };

        return responsiveFontSizes(
            createTheme({
                typography: {
                    fontFamily: getFontFamily(),
                    fontSize: 15,
                    h1: { fontSize: "3.5rem", fontWeight: 200 },
                    h2: { fontSize: "3.0rem", fontWeight: 200 },
                    h3: { fontSize: "2.5rem", fontWeight: 300 },
                    h4: { fontSize: "2.0rem", fontWeight: 400 },
                    h5: { fontSize: "1.5rem", fontWeight: 500 },
                    h6: { fontSize: "1.0rem", fontWeight: 600 },
                    body1: { fontSize: "1rem", lineHeight: 1.5 },
                    body2: { fontSize: "0.9rem", lineHeight: 1.4 },
                    button: {
                        fontSize: "0.8rem",
                        textTransform: "none",
                        textDecoration: "none"
                    }
                },
                components: {
                    // Name of the component
                    MuiButton: {
                        styleOverrides: {
                            // Name of the slot
                            root: ({ theme, ownerState }) => {
                                const baseStyles = {
                                    textTransform: "none",
                                };

                                // Apply contrast detection for outlined buttons
                                if (ownerState.variant === 'outlined') {
                                    const primaryMain = theme.palette.primary.main;
                                    const backgroundDefault = theme.palette.background.default;
                                    const isDarkBackground = getLuminance(backgroundDefault) < 0.5;

                                    // Calculate contrast ratio
                                    const lowContrast = hasLowContrast(primaryMain, backgroundDefault, 3.0);

                                    // Determine which color to use
                                    let borderColor = primaryMain;
                                    let textColor = primaryMain;

                                    if (lowContrast) {
                                        if (isDarkBackground) {
                                            // Dark background - use lighter variant
                                            borderColor = theme.palette.primary.light;
                                            textColor = theme.palette.primary.light;
                                        } else {
                                            // Light background - use darker variant
                                            borderColor = theme.palette.primary.dark;
                                            textColor = theme.palette.primary.dark;
                                        }
                                    }

                                    return {
                                        ...baseStyles,
                                        borderWidth: ".5px",
                                        borderColor: borderColor,
                                        color: textColor,
                                        '&:hover': {
                                            borderColor: borderColor,
                                            backgroundColor: isDarkBackground
                                                ? `${borderColor}15`
                                                : `${borderColor}08`,
                                        },
                                    };
                                }

                                return baseStyles;
                            }
                        }
                    },
                    MuiList: {
                        variants: [
                            {
                                props: { variant: "alternatingbg" },
                                style: {
                                    "& .MuiListItem-root:nth-of-type(odd)": {
                                        backgroundColor: "lightgray"
                                    },
                                    "& .MuiListItem-root:nth-of-type(even)": {
                                        backgroundColor: "white"
                                    },
                                    "& .MuiListItem-root:hover": {
                                        backgroundColor: "gray" // Optional hover effect
                                    }
                                }
                            }
                        ]
                    }
                },
                palette: plt
            })
        );
    }, [darkMode, fontFamily, brandingSettings]);

    console.log("THEME UPDATE", theme);

    return (
        <ThemeContext.Provider value={{
            darkMode,
            setDarkMode,
            setFamily,
            fontFamily,
            brandingSettings,
            updateBrandingSettings,
            updateLogo,
            updateOrganizationName,
            updateOrganizationShortName,
            updateLogoUrl,
            updateLogoDarkUrl,
            updateLogoIconUrl,
            updateLogoIconDarkUrl,
            resetBrandingSettings
        }}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                <GlobalStyles />
                <PrintStyles />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export { ThemeContext, ThemeProvider };

